import React, { useState, type ChangeEvent } from 'react';
import { styled as styled2 } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { colors, elevation as AkElevations } from '@atlaskit/theme';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { borderRadius, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';
import MSTeamsLogo from '@atlassian/jira-icons/src/ui/ms-teams-logomark';
import SlackLogo from '@atlassian/jira-icons/src/ui/slack-logomark';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import msTeamsImage from '../../../../../assets/msteamlogo.svg';
import slackImage from '../../../../../assets/slack.svg';
import { SLACK_APP_TYPE, type AppType } from '../../../../../common/constants';
import { FeatureToggle } from '../../../../../common/ui/feature-toggle-v2';
import { UpgradePlanContent } from '../../../../../common/ui/upgrade-plan-content';
import {
	useChatOpsEntitlement,
	useMsTeamsConnectionStatusInfo,
	useSlackWorkspaceConnections,
} from '../../../../../services/main';
import type { CommunicationWorkspaceConnection } from '../../../../../services/types.tsx';
import messages from './messages';
import { RemoveWorkspaceModal } from './remove-slack-workspace-modal';

type Props = {
	id: string;
	appType: AppType;
	connection: CommunicationWorkspaceConnection;
};
export const Workspace = ({ id, appType, connection }: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const [isRemovalModalOpen, setIsRemoveModalOpen] = useState(false);
	const image = appType === SLACK_APP_TYPE ? slackImage : msTeamsImage;
	const AppLogo = appType === SLACK_APP_TYPE ? SlackLogo : MSTeamsLogo;

	const [, { connectMsTeamstoProject }] = useMsTeamsConnectionStatusInfo();

	const [, { connectSlackToProject }] = useSlackWorkspaceConnections();

	const [{ chatOpsEntitlementEnabled }] = useChatOpsEntitlement();

	const { data: projectContext } = useProjectContext();
	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;

	const onToggleChangeHandler = (
		event: ChangeEvent<HTMLInputElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (connection.enabled) {
			setIsRemoveModalOpen(true);
			fireUIAnalytics(analyticsEvent, 'toggle clicked', `enableDisableJsmProjectTo${appType}`, {
				toggleTo: 'DISABLED',
				containerType: 'project',
				containerId: projectId ?? '',
				source: 'incidentManagementSettingsScreen',
			});
		} else if (SLACK_APP_TYPE === appType) {
			connectSlackToProject({
				connectionId: id ?? '',
				projectId: projectId ?? '',
			});
			fireUIAnalytics(analyticsEvent, 'toggle clicked', `enableDisableJsmProjectTo${appType}`, {
				toggleTo: 'ENABLED',
				containerType: 'project',
				containerId: projectId ?? '',
				source: 'incidentManagementSettingsScreen',
			});
			showFlag({
				type: 'success',
				title: messages.slackConnectionToProjectEnabledSuccessFlagTitle,
				description: messages.slackConnectionToProjectEnabledSuccessFlagText,
			});
		} else {
			connectMsTeamstoProject({
				connectionId: id ?? '',
				projectId: projectId ?? '',
			});
			fireUIAnalytics(analyticsEvent, 'toggle clicked', `enableDisableJsmProjectTo${appType}`, {
				toggleTo: 'ENABLED',
				containerType: 'project',
				containerId: projectId ?? '',
				source: 'incidentManagementSettingsScreen',
			});
		}
	};

	const renderAppLogo = () => {
		if (ff('jfe-asset-consolidation-itsm-agni_po996')) {
			return (
				<Box xcss={AppLogoContainerStyle}>
					<AppLogo label={formatMessage(messages.workspaceImageLabel)} />
				</Box>
			);
		}
		return (
			<WorkspaceIconV2 src={image} role="img" title={formatMessage(messages.workspaceImageLabel)} />
		);
	};

	return (
		<WorkspaceRow>
			<WorkspaceRowInfoContainer>
				{renderAppLogo()}

				<WorkspaceNameV2 tabIndex={0}>{connection.chatSpaceName}</WorkspaceNameV2>
			</WorkspaceRowInfoContainer>
			<WorkspaceRowActionsContainer>
				{!chatOpsEntitlementEnabled ? (
					<FeatureToggle
						disabled
						feature="incident-management"
						popperText={<UpgradePlanContent />}
						focusToggle
					/>
				) : (
					<Toggle
						isChecked={connection.enabled}
						label={formatMessage(messages.toggleLabel)}
						testId={`servicedesk-incident-management-settings.ui.communication-bridges.workplace-connection-settings.workspaces.workspace.${appType}-toggle`}
						onChange={onToggleChangeHandler}
					/>
				)}
			</WorkspaceRowActionsContainer>
			{isRemovalModalOpen && (
				<RemoveWorkspaceModal
					connectionId={id}
					onClose={() => setIsRemoveModalOpen(false)}
					appType={appType}
					workspaceName={connection.workspaceName || connection.chatSpaceName || ''}
				/>
			)}
		</WorkspaceRow>
	);
};

const elevations = { ...AkElevations };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WorkspaceRow = styled2.div<{ elevation?: keyof typeof elevations }>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	flexDirection: 'row',
	minHeight: token('space.500', '40px'),
	marginBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WorkspaceRowInfoContainer = styled2.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WorkspaceRowActionsContainer = styled2.div({
	display: 'flex',
	padding: `0 ${token('space.050', '4px')} ${token('space.050', '4px')} 0`,
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WorkspaceIconV2 = styled2.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
	verticalAlign: 'middle',
	marginLeft: token('space.150', '12px'),
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WorkspaceNameV2 = styled2.div({
	fontWeight: 'normal',
});

const AppLogoContainerStyle = xcss({
	width: token('space.300', '24px'),
	height: token('space.300', '24px'),
	marginLeft: 'space.150',
	marginRight: 'space.100',
});
